import type { CSSProperties } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useInterval, useTimeoutFn } from 'react-use';
import styled, { keyframes } from 'styled-components';

import { ScoreStatus } from '@sorare/core/src/__generated__/globalTypes';
import { OverlapItems } from '@sorare/core/src/atoms/ui/OverlapItems';
import { CardImg } from '@sorare/core/src/components/card/CardImg';
import { LineupCardsLayout } from '@sorare/core/src/components/lineup/LineupCardsLayout';
import { Score } from '@sorare/core/src/components/scoring/Score';
import { StatIcon } from '@sorare/core/src/components/stats/StatIcon';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

const scaleIn = keyframes`
 50% {
    scale: 1.5;
  }
  100% {
    opacity: 1;
    scale: 1;
  }
`;
const statAppear = keyframes`
 0% {
    opacity: 0;
    scale: 2;
  }
 100% {
    opacity: 1;
    scale: 1;
  }   
`;
const LineupWrapper = styled.div`
  border-radius: var(--double-and-a-half-unit);
  background:
    no-repeat center / cover
      url('https://frontend-assets.sorare.com/football/so5_league/seasonal-champions/stadium-all_seasons.jpg?v=1'),
    var(--c-black);
  padding: calc(6 * var(--unit)) var(--triple-unit);
  max-height: calc(var(--100vh) - 300px);
  --total-height: calc(var(--100vh) - 300px);
  aspect-ratio: 1.2;
  max-width: 100%;

  @media ${tabletAndAbove} {
    padding: var(--triple-unit) calc(10 * var(--unit));
  }
`;
const CardWrapper = styled.div`
  position: relative;
  aspect-ratio: var(--card-aspect-ratio);
  margin-bottom: var(--intermediate-unit);
`;
const ScoreWrapper = styled.div`
  position: absolute;
  bottom: calc(-1.5 * var(--unit));
  left: 50%;
  transform: translateX(-50%);
  opacity: 0;
  scale: 0.6;
  transform-origin: center left;
  animation: ${scaleIn} 0.5s 0.3s forwards;
`;
const StatIconsWrapper = styled(OverlapItems)`
  position: absolute;
  top: calc(-1 * var(--unit));
  left: 50%;
  transform: translateX(-50%);
`;
const AnimatedStatIconWrapper = styled.div`
  opacity: 0;
  scale: 0.6;
  animation: ${statAppear} 0.5s var(--stat-icon-animation-delay) forwards;
`;

const appearAnimationDuration = 800;
const scoreAnimationStartingDelay = 500 + appearAnimationDuration;
const otherPlayersAnimationInterval = 1000;
const goalKeeperAnimationDelay =
  scoreAnimationStartingDelay + 2 * otherPlayersAnimationInterval;
const totalAnimationDuration = goalKeeperAnimationDelay + 3000;

const defaultUrls = [
  'https://assets.sorare.com/card/132284e4-4cec-4a79-a92e-a19e049e1acf/picture/tinified-0138743c60ec2ef2857520a078beaabd.png',
  'https://assets.sorare.com/card/4ef4129f-d300-4529-b116-577b8729b6ac/picture/tinified-4eeb98844132852e32b8304c95f71f13.png',
  'https://assets.sorare.com/card/50330042-2877-429f-82c7-a61be92bb084/picture/tinified-f8c68d16fd9e44017d49df7d1b6dfe72.png',
  'https://assets.sorare.com/card/b7f4fc17-68dd-4fbf-8aad-b04235c6e94b/picture/tinified-ff5f740273421131787286328cacf1c8.png',
  'https://assets.sorare.com/card/569e28a7-73c1-4a50-8eb8-9030a1aa9c63/picture/tinified-719b259155680faaeb0bc15416d13ee1.png',
];

const cards = [
  {
    key: 'goalkeeper',
    scores: [35, 15],
    animationDelayMs: goalKeeperAnimationDelay,
    animationIntervalMs: 0,
    animated: true,
    stats: ['three_goals_conceded'] as const,
  },
  {
    key: 'defender',
    scores: [51, 49, 46, 49],
    animationDelayMs: scoreAnimationStartingDelay,
    animationIntervalMs: otherPlayersAnimationInterval,
    animated: false,
  },
  {
    key: 'midfielder',
    scores: [59, 56, 58, 61],
    animationDelayMs: scoreAnimationStartingDelay + 200,
    animationIntervalMs: otherPlayersAnimationInterval,
    animated: false,
  },
  {
    key: 'forward',
    scores: [75, 90],
    animationDelayMs: scoreAnimationStartingDelay,
    animationIntervalMs: 0,
    animated: true,
    stats: ['goals', 'goal_assist'] as const,
  },
  {
    key: 'extra',
    scores: [65, 67, 69, 72],
    animationDelayMs: scoreAnimationStartingDelay + 300,
    animationIntervalMs: otherPlayersAnimationInterval,
    animated: false,
  },
];

type AnimatedScoreProps = {
  scores: number[];
  animationDelayMs: number;
  animationIntervalMs: number;
  animated: boolean;
};
const AnimatedScore = ({
  scores,
  animationDelayMs,
  animationIntervalMs,
  animated,
}: AnimatedScoreProps) => {
  const currentIndexRef = useRef(0);
  const [score, setScore] = useState(scores[0]);
  const [intervalRunning, setIntervalRunning] = useState(false);

  useTimeoutFn(() => {
    const newIndex = currentIndexRef.current + 1;
    setScore(scores[newIndex]);
    currentIndexRef.current = newIndex;
    if (newIndex < scores.length - 1) {
      setIntervalRunning(true);
    }
  }, animationDelayMs);

  useInterval(
    () => {
      if (currentIndexRef.current === scores.length - 1) {
        setIntervalRunning(false);
      } else {
        const newIndex = currentIndexRef.current + 1;
        setScore(scores[newIndex]);
        currentIndexRef.current = newIndex;
      }
    },
    intervalRunning ? animationIntervalMs : null
  );

  return (
    <Score
      score={score}
      scoreStatus={ScoreStatus.FINAL}
      animationDelayMs={0}
      animated={animated}
    />
  );
};

export const PlayLineupAnimation = () => {
  const [loopCount, setLoopCount] = useState(0);
  const {
    flags: { footballOnboardingLineupUrls },
  } = useFeatureFlags();
  const { language } = useIntlContext();

  let lineupUrls = defaultUrls;
  if (footballOnboardingLineupUrls?.[language]?.length) {
    lineupUrls = footballOnboardingLineupUrls[language] as string[];
  } else if (footballOnboardingLineupUrls?.en?.length) {
    lineupUrls = footballOnboardingLineupUrls.en as string[];
  }

  useEffect(() => {
    const timeout = setTimeout(() => {
      setLoopCount(loopCount + 1);
    }, totalAnimationDuration);

    return () => clearTimeout(timeout);
  }, [loopCount]);
  return (
    <LineupWrapper>
      <LineupCardsLayout fillHeightPercent={1}>
        {cards.map((card, index) => {
          const {
            key,
            scores,
            animationDelayMs,
            animationIntervalMs,
            animated,
            stats,
          } = card;
          const url = lineupUrls[index];

          return (
            <CardWrapper key={key}>
              <CardImg width={320} src={url} xl />

              {!!stats?.length && (
                <StatIconsWrapper>
                  {stats.map((stat, statIndex) => (
                    <AnimatedStatIconWrapper
                      key={`${stat}-${loopCount}`}
                      style={
                        {
                          '--stat-icon-animation-delay': `${animationDelayMs + 200 * statIndex}ms`,
                        } as CSSProperties
                      }
                    >
                      <StatIcon statKey={stat} />
                    </AnimatedStatIconWrapper>
                  ))}
                </StatIconsWrapper>
              )}
              <ScoreWrapper key={loopCount}>
                <AnimatedScore
                  scores={scores}
                  animationDelayMs={animationDelayMs}
                  animationIntervalMs={animationIntervalMs}
                  animated={animated}
                />
              </ScoreWrapper>
            </CardWrapper>
          );
        })}
      </LineupCardsLayout>
    </LineupWrapper>
  );
};
